import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Users, Sprout, Camera, TrendingUp, ShieldCheck,
  Phone, Mail, MapPin, Calendar, Crown, Search, Download,
} from "lucide-react";
import { TopBar, BottomNav } from "../components/Nav";
import { API, useAuth } from "../context/AuthContext";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [trend, setTrend] = useState([]);
  const [busy, setBusy] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!user || !user.is_admin) return;
    Promise.all([
      axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)),
      axios.get(`${API}/admin/users`).then((r) => setUsers(r.data)),
      axios.get(`${API}/admin/scans-trend?days=14`).then((r) => setTrend(r.data.trend || [])),
    ])
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [user]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.email, u.phone, u.location].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [users, query]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-6 h-6 animate-spin text-[#52796F]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1B4332] flex items-center justify-center">
            <Crown className="w-5 h-5 text-[#D4A373]" />
          </div>
          <div>
            <h1 className="cd-heading text-3xl font-extrabold">Admin Panel</h1>
            <p className="text-sm text-[#52796F]">Track Hansa users, scans & engagement</p>
          </div>
        </div>

        {busy && (
          <div className="flex justify-center py-20" data-testid="admin-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#52796F]" />
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
            <Stat icon={<Users className="w-5 h-5" />} label="Total users" value={stats.users} testid="stat-users" />
            <Stat icon={<Camera className="w-5 h-5" />} label="Total scans" value={stats.scans} testid="stat-scans" />
            <Stat icon={<Sprout className="w-5 h-5" />} label="Fields" value={stats.fields} testid="stat-fields" />
            <Stat icon={<TrendingUp className="w-5 h-5" />} label="Scans today" value={stats.scans_today} testid="stat-scans-today" />
          </div>
        )}

        {trend.length > 0 && <TrendChart data={trend} />}

        {stats?.top_crops?.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5" data-testid="admin-top-crops">
            <h3 className="cd-heading font-bold text-lg mb-3">Top crops scanned</h3>
            <div className="flex flex-wrap gap-2">
              {stats.top_crops.map((c) => (
                <span
                  key={c.crop}
                  data-testid={`top-crop-${c.crop}`}
                  className="px-3 py-1.5 rounded-full bg-[#F4F1EA] text-sm font-semibold text-[#1B4332] border border-[#E5E0D8]"
                >
                  {c.crop} <span className="text-[#52796F] font-normal">· {c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden" data-testid="admin-users-table">
          <div className="px-5 py-4 border-b border-[#E5E0D8] flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <h3 className="cd-heading font-bold text-lg">
              All users ({filteredUsers.length}
              {query && <span className="text-[#52796F] font-normal"> / {users.length}</span>})
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 sm:flex-none">
                <Search className="w-4 h-4 text-[#52796F] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  data-testid="admin-user-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, phone, email…"
                  className="w-full sm:w-64 bg-white border border-[#E5E0D8] rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-[#1B4332]"
                />
              </div>
              <button
                data-testid="admin-users-csv"
                onClick={() => downloadUsersCsv(filteredUsers)}
                className="flex items-center gap-1.5 bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-xs font-bold px-3 py-2 rounded-full"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wide text-[#52796F]">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Contact</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Joined</th>
                  <th className="text-right px-4 py-3">Scans</th>
                  <th className="text-right px-4 py-3 hidden md:table-cell">Fields</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Last scan</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-[#E5E0D8] hover:bg-[#FDFBF7]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#1B4332] text-[#D4A373] flex items-center justify-center text-xs font-bold">
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-[#1C1C1C] truncate flex items-center gap-1.5">
                            {u.name}
                            {u.is_admin && (
                              <span className="text-[10px] uppercase font-bold bg-[#D4A373] text-[#1C1C1C] px-1.5 py-0.5 rounded-full">
                                admin
                              </span>
                            )}
                          </div>
                          {u.location && (
                            <div className="text-xs text-[#52796F] truncate flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {u.location}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-[#2D3A35]">
                      <div className="space-y-0.5">
                        {u.email && (
                          <div className="text-xs flex items-center gap-1">
                            <Mail className="w-3 h-3 text-[#52796F]" /> {u.email}
                          </div>
                        )}
                        {u.phone && (
                          <div className="text-xs flex items-center gap-1">
                            <Phone className="w-3 h-3 text-[#52796F]" /> {u.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-[#52796F]">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {new Date(u.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-[#1B4332]">{u.scan_count}</td>
                    <td className="px-4 py-3 text-right hidden md:table-cell text-[#2D3A35]">{u.field_count}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#52796F]">
                      {u.last_scan_at ? new Date(u.last_scan_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && !busy && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-[#52796F]" data-testid="admin-users-empty">
                      {query ? "No users match your search." : "No users yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-xs text-[#52796F] flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4" />
          Admins are configured via the <code className="bg-[#F4F1EA] px-1.5 py-0.5 rounded">ADMIN_PHONES</code> env var on the backend.
        </div>
      </main>
      <BottomNav />
    </div>
  );
}

const Stat = ({ icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E5E0D8] p-4">
    <div className="flex items-center gap-2 text-[#52796F] text-xs font-semibold uppercase tracking-wide">
      {icon} {label}
    </div>
    <div className="cd-heading text-3xl font-extrabold mt-1.5 text-[#1B4332]">{value}</div>
  </div>
);

// Lightweight CSS bar chart (no extra deps). Compares scans + new users / day.
const TrendChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => Math.max(d.scans, d.new_users)));
  return (
    <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5" data-testid="admin-trend-chart">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="cd-heading font-bold text-lg">Last 14 days</h3>
          <p className="text-xs text-[#52796F]">Daily scans vs. new signups</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#1B4332]" /> Scans</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#D4A373]" /> New users</span>
        </div>
      </div>
      <div className="flex items-end gap-1.5 h-40">
        {data.map((d) => (
          <div
            key={d.date}
            data-testid={`trend-day-${d.date}`}
            className="flex-1 flex flex-col items-center gap-0.5 group relative"
            title={`${d.date} · ${d.scans} scans · ${d.new_users} new users`}
          >
            <div className="w-full flex items-end justify-center gap-0.5 h-36">
              <div
                className="w-1/2 bg-[#1B4332] rounded-t-sm transition-all"
                style={{ height: `${(d.scans / max) * 100}%`, minHeight: d.scans ? "2px" : 0 }}
              />
              <div
                className="w-1/2 bg-[#D4A373] rounded-t-sm transition-all"
                style={{ height: `${(d.new_users / max) * 100}%`, minHeight: d.new_users ? "2px" : 0 }}
              />
            </div>
            <span className="text-[9px] text-[#52796F]">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

function downloadUsersCsv(users) {
  const headers = ["id", "name", "email", "phone", "location", "scan_count", "field_count", "created_at", "last_scan_at", "is_admin"];
  const rows = users.map((u) =>
    headers.map((h) => {
      const v = u[h];
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(",")
  );
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `hansa-users-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
